/*
  GEMDRAW — Motion primitives

  Small shared helpers for GEMDRAW components that need timing behavior the
  theme does not already provide: countdowns, progress bars, stat counters,
  marquees and the hero depth stage.

  Scroll reveal is deliberately absent. The theme already ships an accessible
  IntersectionObserver reveal (animations.js, the .scroll-trigger and
  .animate--slide-in classes) that is gated behind prefers-reduced-motion.
  GEMDRAW sections reuse it rather than shipping a second reveal system.
*/

(function () {
  'use strict';

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const GemdrawMotion = {
    /* True when the visitor asked for reduced motion. Components must render a
       complete, final, static composition when this is true. */
    prefersReducedMotion() {
      return reducedMotionQuery.matches;
    },

    /* True when the visitor asked for reduced data, where the browser exposes
       it. Used to keep optional media and the interactive map from loading. */
    prefersReducedData() {
      const connection = navigator.connection;
      return Boolean(connection && connection.saveData);
    },

    /* Runs a callback the first time an element scrolls into view, then stops
       observing it. Falls back to running immediately where IntersectionObserver
       is unavailable, so content is never withheld. */
    onceVisible(element, callback, options) {
      if (!element || typeof callback !== 'function') return;

      if (!('IntersectionObserver' in window)) {
        callback(element);
        return;
      }

      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          callback(entry.target);
        });
      }, Object.assign({ rootMargin: '0px 0px -10% 0px' }, options || {}));

      observer.observe(element);
    },

    /*
      Drives a requestAnimationFrame loop only while the element is on screen
      and the document is visible, so nothing keeps painting in a background
      tab. Returns a stop function.

      The tick receives the elapsed milliseconds since the loop started.
    */
    rafWhileVisible(element, tick) {
      if (!element || typeof tick !== 'function') return function () {};

      let frame = null;
      let start = null;
      let onScreen = false;

      function run(now) {
        if (start === null) start = now;
        tick(now - start);
        frame = window.requestAnimationFrame(run);
      }

      function play() {
        if (frame !== null || !onScreen || document.hidden) return;
        frame = window.requestAnimationFrame(run);
      }

      function pause() {
        if (frame === null) return;
        window.cancelAnimationFrame(frame);
        frame = null;
        start = null;
      }

      const observer =
        'IntersectionObserver' in window
          ? new IntersectionObserver(function (entries) {
              onScreen = entries.some(function (entry) {
                return entry.isIntersecting;
              });
              if (onScreen) play();
              else pause();
            })
          : null;

      if (observer) {
        observer.observe(element);
      } else {
        onScreen = true;
        play();
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) pause();
        else play();
      });

      return function stop() {
        pause();
        if (observer) observer.disconnect();
      };
    },

    /*
      Clamped pointer parallax for the campaign hero stage. Translation and
      rotation stay inside the approved envelope, updates are smoothed through
      requestAnimationFrame, and the composition resets cleanly when the pointer
      leaves. Does nothing for reduced motion or on touch-only devices, where a
      single entrance is used instead.
    */
    pointerDepth(element, options) {
      if (!element || this.prefersReducedMotion()) return function () {};
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return function () {};

      const settings = Object.assign({ translate: 8, rotate: 1.2 }, options || {});
      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let frame = null;

      function render() {
        currentX += (targetX - currentX) * 0.08;
        currentY += (targetY - currentY) * 0.08;

        element.style.setProperty('--gd-depth-x', (currentX * settings.translate).toFixed(2) + 'px');
        element.style.setProperty('--gd-depth-y', (currentY * settings.translate).toFixed(2) + 'px');
        element.style.setProperty('--gd-depth-rotate', (currentX * settings.rotate).toFixed(3) + 'deg');

        if (Math.abs(targetX - currentX) > 0.001 || Math.abs(targetY - currentY) > 0.001) {
          frame = window.requestAnimationFrame(render);
        } else {
          frame = null;
        }
      }

      function schedule() {
        if (frame === null) frame = window.requestAnimationFrame(render);
      }

      function onPointerMove(event) {
        const bounds = element.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        targetX = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
        targetY = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
        schedule();
      }

      function onPointerLeave() {
        targetX = 0;
        targetY = 0;
        schedule();
      }

      element.addEventListener('pointermove', onPointerMove);
      element.addEventListener('pointerleave', onPointerLeave);

      return function stop() {
        element.removeEventListener('pointermove', onPointerMove);
        element.removeEventListener('pointerleave', onPointerLeave);
        if (frame !== null) window.cancelAnimationFrame(frame);
      };
    },
  };

  window.GemdrawMotion = GemdrawMotion;
})();
